import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axiosInstance, { API_ENDPOINTS } from "@/config/api";
import {
    ADMIN_CONTROLS_EVENT,
    parseAdminControlsApiResponse,
    readAdminControlsSettings,
    saveAdminControlsSettings,
    type AdminControlsSettings,
} from "@/utils/adminControls.ts";

type PlatformSettingsContextValue = {
    settings: AdminControlsSettings;
    loading: boolean;
    refresh: () => Promise<void>;
};

const PlatformSettingsContext = createContext<PlatformSettingsContextValue | undefined>(undefined);

export const PlatformSettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [settings, setSettings] = useState(readAdminControlsSettings);
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        try {
            const response = await axiosInstance.get(API_ENDPOINTS.auth.platformSettings, {
                headers: { "X-Skip-Auth": "true" },
            });
            setSettings(saveAdminControlsSettings(parseAdminControlsApiResponse(response.data)));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        void refresh().catch(() => setLoading(false));
        const handleUpdate = (event: Event) => {
            const next = (event as CustomEvent<AdminControlsSettings>).detail;
            if (next) setSettings(next);
        };
        const handleVisibilityChange = () => {
            if (document.visibilityState === "visible") void refresh().catch(() => undefined);
        };
        const refreshInterval = window.setInterval(() => void refresh().catch(() => undefined), 60_000);
        window.addEventListener(ADMIN_CONTROLS_EVENT, handleUpdate);
        window.addEventListener("focus", refresh);
        document.addEventListener("visibilitychange", handleVisibilityChange);
        return () => {
            window.clearInterval(refreshInterval);
            window.removeEventListener(ADMIN_CONTROLS_EVENT, handleUpdate);
            window.removeEventListener("focus", refresh);
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, [refresh]);

    useEffect(() => {
        document.querySelectorAll<HTMLLinkElement>("link[rel='icon'], link[rel='shortcut icon'], link[rel='apple-touch-icon']")
            .forEach((element) => { element.href = settings.compactLogoUrl || settings.logoUrl; });
        document.querySelectorAll<HTMLMetaElement>("meta[property='og:image'], meta[name='twitter:image']")
            .forEach((element) => element.setAttribute("content", settings.socialImageUrl || settings.logoUrl));
    }, [settings]);

    const value = useMemo(() => ({ settings, loading, refresh }), [settings, loading, refresh]);
    return <PlatformSettingsContext.Provider value={value}>{children}</PlatformSettingsContext.Provider>;
};

export const usePlatformSettings = (): PlatformSettingsContextValue => {
    const context = useContext(PlatformSettingsContext);
    if (!context) throw new Error("usePlatformSettings must be used within PlatformSettingsProvider");
    return context;
};
